import {  t  } from "@/client/libs/dummy-i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@stackmorph/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { addMember } from "@/client/services/organization/organization";

type AddMemberDialogProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  organizationId: string;
};

export const AddMemberDialog = ({ isOpen, setIsOpen, organizationId }: AddMemberDialogProps) => {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");

  const { mutate: addMemberMutation, isPending } = useMutation({
    mutationFn: async () => {
      await addMember(organizationId, email, role);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["organization", organizationId] });
      setIsOpen(false);
      setEmail("");
      setRole("member");
    },
    onError: (error: Error) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const message = (error as any).response?.data?.message ?? t`Failed to add member`;
      alert(message);
    },
  });

  const handleSubmit = () => {
    if (!email) return;
    addMemberMutation();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t`Add Member`}</DialogTitle>
          <DialogDescription>
            {t`Invite a member to join this organization by entering their email address.`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="email">{t`Email Address`}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              placeholder="member@example.com"
              onChange={(e) => { setEmail(e.target.value); }}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="role">{t`Role`}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">{t`Member`}</SelectItem>
                <SelectItem value="owner">{t`Owner`}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => { setIsOpen(false); }}>
            {t`Cancel`}
          </Button>
          <Button disabled={isPending || !email} onClick={handleSubmit}>
            {t`Add Member`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
